import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Alert, Loader, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Webhook management (`/app-webhook-endpoint`) — register consumer endpoints for AI
 * voice-call events, see the delivery log, retry failures. The signing secret is
 * shown EXACTLY ONCE (on create or rotate) — after that the server only ever reports
 * has_secret (models/AppWebhookEndpoint.model.js strips the ciphertext), matching the
 * connections-credentials stance.
 */
const EVENT_OPTIONS = [
  { value: 'call.completed', label: 'call.completed — a call finished and its data is stored' },
  { value: 'call.failed', label: 'call.failed — a call failed / no answer / busy' },
  { value: 'call.data_collected', label: 'call.data_collected — collected JSON is available' },
  { value: 'campaign.completed', label: 'campaign.completed — every call in a campaign landed' },
  { value: 'webhook.test', label: 'webhook.test — test pings from this screen' },
];

function DeliveryStatusBadge({ status }) {
  const variant = { delivered: 'success', delivering: 'primary', pending: 'secondary', failed: 'warning', exhausted: 'error' };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status}</Badge>;
}

function SecretReveal({ secret, onDismiss }) {
  return (
    <Alert variant="warning" title="Signing secret — copy it now, it will not be shown again">
      <code className="block break-all text-xs bg-[var(--app-surface)] rounded p-2 my-2">{secret}</code>
      <p className="text-xs mb-2">Verify deliveries with HMAC-SHA256: signature header <code>X-Webhook-Signature: t=&lt;unix&gt;,v1=hex(hmac(&#96;$&#123;t&#125;.$&#123;body&#125;&#96;, secret))</code>.</p>
      <Button size="sm" variant="secondary" onClick={onDismiss}>I saved it</Button>
    </Alert>
  );
}

function EndpointForm({ initial, onSubmit, onClose, busy }) {
  const [name, setName] = useState(initial?.name || '');
  const [url, setUrl] = useState(initial?.url || '');
  const [events, setEvents] = useState(initial?.events || ['call.completed']);
  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{initial ? `Edit "${initial.name}"` : 'Register webhook endpoint'}</h2>
        <button className="text-sm text-secondary-500" onClick={onClose}>Close</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="URL (https)" placeholder="https://example.com/hooks/ai-calls" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>
      <Select
        label="Subscribed events"
        multiSelect
        searchable
        options={EVENT_OPTIONS}
        value={events}
        onChange={(v) => setEvents(Array.isArray(v) ? v : [])}
      />
      <div className="text-right">
        <Button onClick={() => onSubmit({ name, url, events })} disabled={busy || !name.trim() || !url.trim() || !events.length}>
          {busy ? 'Saving…' : 'Save endpoint'}
        </Button>
      </div>
    </Card>
  );
}

function DeliveriesTable({ endpointId, refreshKey }) {
  const { fetchApi } = useAppApi();
  const [deliveries, setDeliveries] = useState(null);
  const [err, setErr] = useState('');

  const load = () => fetchApi('voiceWebhookDeliveries', endpointId)
    .then((rows) => setDeliveries(Array.isArray(rows) ? rows : []))
    .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message));

  useEffect(() => { load(); }, [endpointId, refreshKey]);

  const redeliver = async (deliveryId) => {
    try {
      await fetchApi('voiceWebhookRedeliver', deliveryId);
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  if (err) return <Alert variant="error" title="Could not load deliveries">{err}</Alert>;
  if (!deliveries) return <Loader text="Loading deliveries…" />;
  if (!deliveries.length) return <p className="text-sm text-secondary-500 py-2">No deliveries yet — they appear when subscribed events fire (or use "Send test").</p>;

  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-secondary-500">
        <tr>
          <th className="py-1">Event</th><th className="py-1">Status</th><th className="py-1">HTTP</th>
          <th className="py-1">Attempts</th><th className="py-1">When</th><th className="py-1" />
        </tr>
      </thead>
      <tbody>
        {deliveries.map((d) => (
          <tr key={d.id} className="border-t border-[var(--app-border)]" title={d.last_error || ''}>
            <td className="py-1 font-mono text-xs">{d.event_type}</td>
            <td className="py-1"><DeliveryStatusBadge status={d.status} /></td>
            <td className="py-1">{d.last_status_code || '—'}</td>
            <td className="py-1">{d.attempt_count}</td>
            <td className="py-1 text-secondary-500">{d.created_at ? new Date(d.created_at).toLocaleString() : ''}</td>
            <td className="py-1">
              {['failed', 'exhausted'].includes(d.status) && (
                <Can module="calls" action="configure">
                  <button className="text-xs text-primary-600 hover:underline" onClick={() => redeliver(d.id)}>Redeliver</button>
                </Can>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function WebhooksPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [endpoints, setEndpoints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [secret, setSecret] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const load = () => fetchApi('voiceWebhookList')
    .then((rows) => setEndpoints(Array.isArray(rows) ? rows : []))
    .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const filteredEndpoints = applicationId
    ? endpoints.filter(e => e.application_id == null || e.application_id === Number(applicationId))
    : endpoints;

  const submit = async (data) => {
    setBusy(true);
    setErr('');
    try {
      const res = editing
        ? await fetchApi('voiceWebhookUpdate', { id: editing.id, ...data })
        : await fetchApi('voiceWebhookCreate', data);
      if (res.secret) setSecret(res.secret);
      setShowForm(false);
      setEditing(null);
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const act = async (endpoint, action) => {
    setErr('');
    try {
      if (action === 'test') {
        await fetchApi('voiceWebhookTest', endpoint.id);
        setExpandedId(endpoint.id);
        setRefreshKey((k) => k + 1);
      } else if (action === 'toggle') {
        await fetchApi('voiceWebhookUpdate', { id: endpoint.id, is_active: !endpoint.is_active });
        load();
      } else if (action === 'rotate') {
        const res = await fetchApi('voiceWebhookUpdate', { id: endpoint.id, rotate_secret: true });
        if (res.secret) setSecret(res.secret);
      } else if (action === 'delete') {
        await fetchApi('voiceWebhookDelete', endpoint.id);
        load();
      }
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const expandedRow = endpoints.find((e) => e.id === expandedId) || null;

  const columns = [
    { key: 'name', label: 'Name', render: (e) => <button onClick={() => nav(`/app-webhook-endpoint/${e.id}`)} className="font-medium text-primary-600 hover:underline text-left">{e.name}</button> },
    { key: 'url', label: 'URL', render: (e) => <span className="block max-w-[260px] truncate font-mono text-xs" title={e.url}>{e.url}</span> },
    { key: 'events', label: 'Events', sortable: false, searchable: false, render: (e) => <span className="text-xs">{(e.events || []).join(', ')}</span> },
    { key: 'is_active', label: 'Active', searchable: false, render: (e) => (e.is_active ? '✓' : '—') },
    {
      key: 'row_actions',
      label: 'Actions',
      sortable: false,
      searchable: false,
      render: (endpoint) => (
        <div className="flex flex-wrap gap-2 text-xs">
          <button className="text-primary-600 hover:underline" onClick={() => setExpandedId(expandedId === endpoint.id ? null : endpoint.id)}>
            Deliveries
          </button>
          <Can module="calls" action="configure">
            <button className="text-primary-600 hover:underline" onClick={() => act(endpoint, 'test')}>Send test</button>
            <button className="text-primary-600 hover:underline" onClick={() => { setEditing(endpoint); setShowForm(true); }}>Edit</button>
            <button className="text-primary-600 hover:underline" onClick={() => act(endpoint, 'toggle')}>{endpoint.is_active ? 'Deactivate' : 'Activate'}</button>
            <button className="text-primary-600 hover:underline" onClick={() => act(endpoint, 'rotate')}>Rotate secret</button>
            <button className="text-red-600 hover:underline" onClick={() => act(endpoint, 'delete')}>Delete</button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Webhooks"
        subtitle={<>HMAC-signed event deliveries to your connected applications when AI voice calls complete.</>}
        actions={(
          <Can module="calls" action="configure">
            <Button size="sm" onClick={() => { setEditing(null); setShowForm((v) => !v); }}>Register endpoint</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {secret && <SecretReveal secret={secret} onDismiss={() => setSecret(null)} />}
      {showForm && <EndpointForm initial={editing} onSubmit={submit} onClose={() => { setShowForm(false); setEditing(null); }} busy={busy} />}

      <DataTable
        data={filteredEndpoints}
        columns={columns}
        loading={loading}
        emptyText="No webhook endpoints registered."
        search={{ placeholder: 'Search endpoints…', keys: ['name', 'url'] }}
        onReload={load}
        toolbar={(
          <div className="w-48 sm:w-56">
            <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
          </div>
        )}
      />

      {/* Kept as a panel below the table rather than <Table>'s own expandable row: "Send
          test" force-opens this endpoint's deliveries, and <Table> owns its expansion
          state internally with no way to drive it from here. */}
      {expandedRow && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Deliveries — {expandedRow.name}</h2>
            <Button size="sm" variant="secondary" onClick={() => setExpandedId(null)}>Close</Button>
          </div>
          <DeliveriesTable endpointId={expandedRow.id} refreshKey={refreshKey} />
        </div>
      )}
    </PageShell>
  );
}
